import React, { useLayoutEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Headroom from 'react-headroom';
import { Header } from '../Header';
import { BottomNav } from '../BottomNav';
import { SidebarNav } from '../SidebarNav';
import { RouterNavigationSwiper } from '@/components/RouterNavigationSwiper';
import { usePageConfigContext } from '@/contexts/PageConfigContext';
import { PageConfigProvider } from '@/contexts/PageConfigProvider';
import { HeaderSearchProvider, useHeaderSearchMode } from '@/contexts/HeaderSearchContext';
import { useAuth } from '@/hooks/useAuth';
import { useSwipeBack } from '@/hooks/useSwipeBack';
import { useAppBack } from '@/hooks/useAppBack';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import { useIOSClickFallback } from '@/hooks/useIOSClickFallback';
import { useKeyboardFieldFocused } from '@/hooks/useTelegramKeyboardScrollFix';
import { isContentLayoutRoute } from '@/utils/routePatterns';
import styles from './styles.module.scss';

// URL fallback for nav-strip visibility — guarantees correct first frame
// before child layouts' useLayoutEffect runs (prevents one-frame flash on
// /dashboard cold reload, since location.pathname !== '/' would otherwise
// evaluate true).
function routeShowsNavigation(pathname: string): boolean {
  if (pathname === '/' || pathname === '/dashboard') return false;
  if (pathname === '/tournaments/create') return false;
  if (pathname === '/verify-email' || pathname === '/account-blocked') return false;
  if (pathname.startsWith('/invite/') || pathname.startsWith('/join/')) return false;
  return true;
}

const AppLayoutShell: React.FC = () => {
  const { config } = usePageConfigContext();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const isMobile = useBreakpointDown('lg');
  // Telegram mobile: the header stays put while a field is focused — the
  // keyboard scroll must not slide it away (`useTelegramKeyboardScrollFix`).
  const pinHeader = useKeyboardFieldFocused();
  // Search mode keeps the header (and the search field in it) on screen.
  const { active: searchActive, close: closeSearch } = useHeaderSearchMode();

  // Back is derived from the URL alone (`utils/backRoute`): right on the
  // first frame, one rule for every page. The dashboard keeps the burger.
  const { canGoBack, goBack } = useAppBack();
  // URL fallback: the solid header is right before the page's config applies.
  const showSolidHeader = config.headerSolid || isContentLayoutRoute(location.pathname);

  // Edge swipe goes back like the header button; header search mode is left
  // first, like its back arrow.
  useSwipeBack({
    onSwipeBack: () => {
      if (searchActive) {
        closeSearch({ returnFocus: true });
        return;
      }
      goBack();
    },
  });

  // iOS-only: synthesise click on touchend if WebKit suppressed the native one
  // (happens after /dashboard re-mount while widget tree is still mutating).
  useIOSClickFallback();

  // Sync `--header-active` with actual headroom-wrapper height before first paint.
  // Without this, the var is stale until Headroom fires its first onPin/onUnpin
  // callback, which can leave a wrong header offset on initial route mount.
  useLayoutEffect(() => {
    const header = document.querySelector('.headroom-wrapper') as HTMLElement | null;
    if (header) {
      document.documentElement.style.setProperty('--header-active', `${header.offsetHeight || 0}px`);
    }
  }, []);

  const onPin = () => {
    const header = document.querySelector('.headroom-wrapper') as HTMLElement | null;
    if (header) {
      document.documentElement.style.setProperty('--header-active', `${header.offsetHeight || 0}px`);
    }
  };

  const onUnpin = () => {
    document.documentElement.style.setProperty('--header-active', '0px');
  };

  const shouldShowNavigation = isAuthenticated && (config.showNavigation !== undefined
    ? config.showNavigation
    : routeShowsNavigation(location.pathname));

  return (
    <div className={`app ${config.fluid ? 'fluid' : ''}`}>
      <SidebarNav />

      <div className="app_container">
        <div className="app_container-content d-flex flex-column flex-1">
          {config.showHeader && (
            isMobile ? (
              <Headroom
                pin={pinHeader || searchActive}
                onPin={onPin}
                onUnpin={onUnpin}
                style={{ transition: 'all .3s ease-in-out', zIndex: 100 }}
              >
                <Header
                  showBackButton={canGoBack}
                  onBackClick={goBack}
                  solid={showSolidHeader}
                  search={config.headerSearch}
                  searchActive={searchActive}
                  hideLanguageSwitcher={config.hideLanguageSwitcher}
                />
              </Headroom>
            ) : (
              <Header
                showBackButton={canGoBack}
                onBackClick={goBack}
                solid={showSolidHeader}
                search={config.headerSearch}
                searchActive={searchActive}
                hideLanguageSwitcher={config.hideLanguageSwitcher}
              />
            )
          )}
          {/* Nav strip lives OUTSIDE `.layout` so its presence/absence does not
              interact with `.layout`'s `gap: 20px` (no empty-gap regression on
              routes without a strip). Conditional render keeps DOM minimal. */}
          {shouldShowNavigation && (
            <div className={styles.navStrip}>
              <RouterNavigationSwiper hiddenPaths={config.hiddenNavPaths} />
            </div>
          )}
          <div className={`layout ${config.className}`}>
            <Outlet />
          </div>
        </div>
      </div>

      {config.showBottomNav && <BottomNav />}
    </div>
  );
};

/**
 * Route-level layout for the standard app shell.
 * Wraps child routes with Header + SidebarNav + BottomNav.
 * Pages configure their specific props via usePageConfig() hook.
 */
export const AppLayout: React.FC = () => {
  return (
    <PageConfigProvider>
      <HeaderSearchProvider>
        <AppLayoutShell />
      </HeaderSearchProvider>
    </PageConfigProvider>
  );
};

export default AppLayout;
